import { useState, useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { 
  FileText, Phone, AlertTriangle, CheckCircle2, XCircle,
  TrendingUp, TrendingDown, Eye, Filter, Download,
  ArrowUpDown, ArrowUp, ArrowDown, FileAudio, Brain,
  BarChart3, Calendar, Target, Activity, PieChart
} from 'lucide-react';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart as RechartsPie, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { getCallLogNotes, getCallLogComplianceStats } from '@/lib/api';

// Chart colors
const CHART_COLORS = {
  best: '#10b981',
  good: '#3b82f6', 
  improvement: '#f59e0b',
  high_alert: '#ef4444',
  critical: '#dc2626',
  medium: '#f97316',
  primary: '#8b5cf6',
  secondary: '#06b6d4'
};

// Sortable header component
const SortableHeader = ({ label, sortKey, currentSort, onSort }) => {
  const isActive = currentSort.key === sortKey;
  const Icon = isActive ? (currentSort.direction === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
  
  return (
    <button
      className="flex items-center gap-1 hover:text-foreground transition-colors font-medium"
      onClick={() => onSort(sortKey)}
    >
      {label}
      <Icon className={`h-4 w-4 ${isActive ? 'text-blue-600' : 'text-muted-foreground'}`} />
    </button>
  );
};

export default function UserReports() {
  const { user } = useOutletContext();
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('reports');
  
  // Call Log Notes data
  const [callLogs, setCallLogs] = useState([]);
  const [complianceStats, setComplianceStats] = useState(null);
  
  // Filters
  const [filters, setFilters] = useState({
    has_notes: 'all',
    date_from: '',
    date_to: ''
  });
  
  // Analytics filters
  const [timeRange, setTimeRange] = useState('this_month');
  
  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: 'call_date', direction: 'desc' });
  
  // Detail view
  const [selectedLog, setSelectedLog] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadCallLogs();
  }, [filters, user]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      // Get stats for current user
      const statsRes = await getCallLogComplianceStats({ user_id: user?.id });
      setComplianceStats(statsRes.data);
      await loadCallLogs();
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadCallLogs = async () => {
    if (!user?.id) return;
    
    try {
      const params = { user_id: user.id };
      if (filters.has_notes !== 'all') params.has_notes = filters.has_notes === 'yes';
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      
      const res = await getCallLogNotes(params);
      setCallLogs(res.data);
    } catch (error) {
      console.error('Failed to load call logs:', error);
    }
  };

  const exportToCSV = () => {
    const headers = ['Date', 'Time', 'Applicant', 'Phone', 'Call Type', 'Duration', 'Classification', 'Alert Level', 'Has Notes'];
    const rows = sortedCallLogs.map(log => [
      new Date(log.call_date).toLocaleDateString(),
      new Date(log.call_date).toLocaleTimeString(),
      log.applicant_name || '-',
      log.phone_number,
      log.call_type,
      log.duration || '-',
      log.classification || '-',
      log.alert_level || '-',
      log.has_notes || log.manually_marked_compliant ? 'Yes' : 'No'
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `my_call_logs_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sort call logs
  const sortedCallLogs = useMemo(() => {
    if (!callLogs.length) return [];
    
    return [...callLogs].sort((a, b) => {
      const { key, direction } = sortConfig;
      let aVal, bVal;
      
      switch (key) {
        case 'call_date':
          aVal = new Date(a.call_date).getTime();
          bVal = new Date(b.call_date).getTime();
          break;
        case 'applicant_name':
          aVal = (a.applicant_name || '').toLowerCase();
          bVal = (b.applicant_name || '').toLowerCase();
          break;
        case 'classification':
          const classOrder = { best: 0, good: 1, improvement: 2, high_alert: 3 };
          aVal = classOrder[a.classification] ?? 99;
          bVal = classOrder[b.classification] ?? 99;
          break;
        case 'alert_level':
          const alertOrder = { good: 0, improvement: 1, medium: 2, critical: 3 };
          aVal = alertOrder[a.alert_level] ?? 99;
          bVal = alertOrder[b.alert_level] ?? 99;
          break;
        default:
          aVal = a[key] || '';
          bVal = b[key] || '';
      }
      
      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [callLogs, sortConfig]);

  // Analytics data calculations
  const filteredLogsForAnalytics = useMemo(() => {
    const now = new Date();
    let startDate;
    
    switch (timeRange) {
      case 'this_month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'last_month':
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        break;
      case 'last_3_months':
        startDate = new Date(now.getFullYear(), now.getMonth() - 2, 1);
        break;
      case 'this_year':
        startDate = new Date(now.getFullYear(), 0, 1);
        break;
      default:
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
    }
    
    return callLogs.filter(log => new Date(log.call_date) >= startDate);
  }, [callLogs, timeRange]);

  // Classification distribution for pie chart
  const classificationData = useMemo(() => {
    const counts = { best: 0, good: 0, improvement: 0, high_alert: 0 };
    filteredLogsForAnalytics.forEach(log => {
      if (log.classification && counts[log.classification] !== undefined) {
        counts[log.classification]++;
      }
    });
    return Object.entries(counts).map(([name, value]) => ({
      name: name.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
      value,
      color: CHART_COLORS[name]
    })).filter(d => d.value > 0);
  }, [filteredLogsForAnalytics]);

  // Daily trend data
  const dailyTrendData = useMemo(() => {
    const dailyCounts = {};
    filteredLogsForAnalytics.forEach(log => {
      const date = new Date(log.call_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      if (!dailyCounts[date]) {
        dailyCounts[date] = { date, total: 0, compliant: 0 };
      }
      dailyCounts[date].total++;
      if (log.has_notes || log.manually_marked_compliant) {
        dailyCounts[date].compliant++;
      }
    });
    return Object.values(dailyCounts).slice(-14);
  }, [filteredLogsForAnalytics]);

  // Score trend over time
  const scoreTrendData = useMemo(() => {
    const byDate = {};
    filteredLogsForAnalytics.forEach(log => {
      if (log.average_score) {
        const date = new Date(log.call_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        if (!byDate[date]) {
          byDate[date] = { date, scores: [], count: 0 };
        }
        byDate[date].scores.push(log.average_score);
        byDate[date].count++;
      }
    });
    
    return Object.values(byDate).map(d => ({
      date: d.date,
      avgScore: d.scores.length > 0 ? (d.scores.reduce((a, b) => a + b, 0) / d.scores.length).toFixed(1) : 0,
      calls: d.count
    })).slice(-14);
  }, [filteredLogsForAnalytics]);

  // Call type distribution
  const callTypeData = useMemo(() => {
    const counts = {};
    filteredLogsForAnalytics.forEach(log => {
      const type = log.call_type || 'other';
      counts[type] = (counts[type] || 0) + 1;
    });
    return Object.entries(counts).map(([name, value]) => ({
      name: name.charAt(0).toUpperCase() + name.slice(1),
      value
    }));
  }, [filteredLogsForAnalytics]);

  // Calculate compliance rate for analytics
  const analyticsComplianceRate = useMemo(() => {
    if (filteredLogsForAnalytics.length === 0) return 0;
    const compliant = filteredLogsForAnalytics.filter(l => l.has_notes || l.manually_marked_compliant).length;
    return Math.round((compliant / filteredLogsForAnalytics.length) * 100);
  }, [filteredLogsForAnalytics]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-24" />
        <div className="grid grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-20" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg p-6 text-white">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-card/20 rounded-lg">
            <FileText className="h-8 w-8" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">My Reports</h1>
            <p className="text-blue-100 mt-1">
              View your call performance and compliance reports
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="reports">Call Logs</TabsTrigger>
          <TabsTrigger value="analytics" className="flex items-center gap-1">
            <BarChart3 className="h-4 w-4" />
            Analytics
          </TabsTrigger>
        </TabsList>

        {/* Reports Tab */}
        <TabsContent value="reports" className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Calls</p>
                    <p className="text-2xl font-bold">{complianceStats?.total_calls || 0}</p>
                  </div>
                  <Phone className="h-8 w-8 text-blue-300" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-emerald-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Compliant</p>
                    <p className="text-2xl font-bold text-emerald-600">{complianceStats?.calls_with_notes || 0}</p>
                  </div>
                  <CheckCircle2 className="h-8 w-8 text-emerald-300" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-red-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Non-Compliant</p>
                    <p className="text-2xl font-bold text-red-600">{complianceStats?.calls_without_notes || 0}</p>
                  </div>
                  <XCircle className="h-8 w-8 text-red-300" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Compliance Rate</p>
                    <p className={`text-2xl font-bold ${(complianceStats?.compliance_rate || 0) >= 80 ? 'text-emerald-600' : 'text-amber-600'}`}>
                      {complianceStats?.compliance_rate || 0}%
                    </p>
                  </div>
                  {(complianceStats?.compliance_rate || 0) >= 80 ? (
                    <TrendingUp className="h-8 w-8 text-emerald-300" />
                  ) : (
                    <TrendingDown className="h-8 w-8 text-amber-300" />
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters & Actions */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4 items-center justify-between">
                <div className="flex flex-wrap gap-4 items-center">
                  <div className="flex items-center gap-2">
                    <Filter className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Filters:</span>
                  </div>
                  
                  <Select 
                    value={filters.has_notes} 
                    onValueChange={(v) => setFilters({...filters, has_notes: v})}
                  >
                    <SelectTrigger className="w-[160px]">
                      <SelectValue placeholder="All Calls" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Calls</SelectItem>
                      <SelectItem value="yes">With Notes</SelectItem>
                      <SelectItem value="no">Without Notes</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  <Input
                    type="date"
                    value={filters.date_from}
                    onChange={(e) => setFilters({...filters, date_from: e.target.value})}
                    className="w-[150px]"
                    placeholder="From"
                  />
                  <Input
                    type="date"
                    value={filters.date_to}
                    onChange={(e) => setFilters({...filters, date_to: e.target.value})}
                    className="w-[150px]"
                    placeholder="To"
                  />
                </div>
                
                <Button variant="outline" onClick={exportToCSV}>
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Call Logs Table */}
          <Card>
            <CardHeader>
              <CardTitle>My Call Logs</CardTitle>
              <CardDescription>Your call history with compliance status</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      <SortableHeader label="Date" sortKey="call_date" currentSort={sortConfig} onSort={handleSort} />
                    </TableHead>
                    <TableHead>Time</TableHead>
                    <TableHead>
                      <SortableHeader label="Applicant" sortKey="applicant_name" currentSort={sortConfig} onSort={handleSort} />
                    </TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Call Type</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>
                      <SortableHeader label="Classification" sortKey="classification" currentSort={sortConfig} onSort={handleSort} />
                    </TableHead>
                    <TableHead>
                      <SortableHeader label="Alert" sortKey="alert_level" currentSort={sortConfig} onSort={handleSort} />
                    </TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedCallLogs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center py-12 text-muted-foreground">
                        No call logs found
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedCallLogs.map((log) => (
                      <TableRow 
                        key={log.id} 
                        className={`cursor-pointer hover:bg-muted/50 ${
                          !log.has_notes && !log.manually_marked_compliant ? 'bg-red-50/50' : ''
                        }`}
                        onClick={() => setSelectedLog(log)}
                      >
                        <TableCell>{new Date(log.call_date).toLocaleDateString()}</TableCell>
                        <TableCell>{new Date(log.call_date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</TableCell>
                        <TableCell className="font-medium">{log.applicant_name || '-'}</TableCell>
                        <TableCell>{log.phone_number}</TableCell>
                        <TableCell className="capitalize">{log.call_type}</TableCell>
                        <TableCell>{log.duration || '-'}</TableCell>
                        <TableCell>
                          {log.classification && (
                            <Badge className={`classification-${log.classification}`}>
                              {log.classification.replace('_', ' ')}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {log.alert_level && (
                            <Badge className={
                              log.alert_level === 'critical' ? 'bg-red-500' :
                              log.alert_level === 'medium' ? 'bg-amber-500' :
                              log.alert_level === 'improvement' ? 'bg-blue-500' :
                              'bg-emerald-500'
                            }>
                              {log.alert_level}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {log.has_notes || log.manually_marked_compliant ? (
                            <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                          ) : (
                            <XCircle className="h-5 w-5 text-red-500" />
                          )}
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="icon">
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-6">
          {/* Analytics Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4 items-center">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Time Range:</span>
                </div>
                <Select value={timeRange} onValueChange={setTimeRange}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="this_month">This Month</SelectItem>
                    <SelectItem value="last_month">Last Month</SelectItem>
                    <SelectItem value="last_3_months">Last 3 Months</SelectItem>
                    <SelectItem value="this_year">This Year</SelectItem>
                  </SelectContent>
                </Select>
                
                <Badge variant="outline" className="ml-auto">
                  {filteredLogsForAnalytics.length} calls in range
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Analytics Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-purple-50 to-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Calls</p>
                    <p className="text-3xl font-bold text-purple-600">{filteredLogsForAnalytics.length}</p>
                  </div>
                  <Phone className="h-8 w-8 text-purple-300" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-emerald-50 to-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Compliant</p>
                    <p className="text-3xl font-bold text-emerald-600">
                      {filteredLogsForAnalytics.filter(l => l.has_notes || l.manually_marked_compliant).length}
                    </p>
                  </div>
                  <CheckCircle2 className="h-8 w-8 text-emerald-300" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-blue-50 to-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Compliance Rate</p>
                    <p className="text-3xl font-bold text-blue-600">{analyticsComplianceRate}%</p>
                  </div>
                  <Target className="h-8 w-8 text-blue-300" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-gradient-to-br from-amber-50 to-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Avg Score</p>
                    <p className="text-3xl font-bold text-amber-600">
                      {filteredLogsForAnalytics.length > 0 
                        ? (filteredLogsForAnalytics.reduce((a, b) => a + (b.average_score || 0), 0) / filteredLogsForAnalytics.filter(l => l.average_score).length || 0).toFixed(1)
                        : '0.0'}
                    </p>
                  </div>
                  <Activity className="h-8 w-8 text-amber-300" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Charts Row 1 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Score Trend Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5 text-purple-600" />
                  Score Trend
                </CardTitle>
                <CardDescription>Your call scores over time</CardDescription>
              </CardHeader>
              <CardContent>
                {scoreTrendData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <LineChart data={scoreTrendData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                      <YAxis domain={[0, 10]} tick={{ fontSize: 12 }} stroke="#94a3b8" />
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: 'white', 
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px'
                        }}
                      />
                      <Legend />
                      <Line type="monotone" dataKey="avgScore" stroke="#8b5cf6" strokeWidth={2} name="Avg Score" dot={{ fill: '#8b5cf6' }} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[280px] flex items-center justify-center text-muted-foreground">
                    No score data available for selected range
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Classification Pie Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PieChart className="h-5 w-5 text-blue-600" />
                  Call Classification
                </CardTitle>
                <CardDescription>Distribution by quality classification</CardDescription>
              </CardHeader>
              <CardContent>
                {classificationData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <RechartsPie>
                      <Pie
                        data={classificationData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {classificationData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </RechartsPie>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[280px] flex items-center justify-center text-muted-foreground">
                    No classification data available
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Charts Row 2 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Daily Compliance Trend */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-emerald-600" />
                  Daily Compliance Trend
                </CardTitle>
                <CardDescription>Compliant calls per day</CardDescription>
              </CardHeader>
              <CardContent>
                {dailyTrendData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <AreaChart data={dailyTrendData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                      <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" />
                      <Tooltip />
                      <Legend />
                      <Area type="monotone" dataKey="compliant" stackId="1" stroke="#10b981" fill="#10b981" fillOpacity={0.6} name="Compliant" />
                      <Area type="monotone" dataKey="total" stackId="2" stroke="#94a3b8" fill="#94a3b8" fillOpacity={0.3} name="Total" />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[280px] flex items-center justify-center text-muted-foreground">
                    No data available for selected range
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Call Type Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-amber-600" />
                  Call Type Distribution
                </CardTitle>
                <CardDescription>Breakdown by call type</CardDescription>
              </CardHeader>
              <CardContent>
                {callTypeData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={callTypeData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                      <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" />
                      <Tooltip />
                      <Bar dataKey="value" fill="#f59e0b" radius={[4, 4, 0, 0]} name="Calls" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[280px] flex items-center justify-center text-muted-foreground">
                    No call type data available
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Call Detail Dialog */}
      <Dialog open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Phone className="h-5 w-5" />
              Call Details
            </DialogTitle>
            <DialogDescription>
              {selectedLog && new Date(selectedLog.call_date).toLocaleString()}
            </DialogDescription>
          </DialogHeader>
          {selectedLog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Applicant</p>
                  <p className="font-medium">{selectedLog.applicant_name || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Phone Number</p>
                  <p className="font-medium">{selectedLog.phone_number}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Call Type</p>
                  <p className="font-medium capitalize">{selectedLog.call_type}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Duration</p>
                  <p className="font-medium">{selectedLog.duration || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Classification</p>
                  {selectedLog.classification && (
                    <Badge className={`classification-${selectedLog.classification}`}>
                      {selectedLog.classification.replace('_', ' ')}
                    </Badge>
                  )}
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Alert Level</p>
                  {selectedLog.alert_level && (
                    <Badge className={
                      selectedLog.alert_level === 'critical' ? 'bg-red-500' :
                      selectedLog.alert_level === 'medium' ? 'bg-amber-500' :
                      'bg-emerald-500'
                    }>
                      {selectedLog.alert_level}
                    </Badge>
                  )}
                </div>
              </div>
              
              <div>
                <p className="text-sm text-muted-foreground mb-2">Compliance Status</p>
                <div className={`p-4 rounded-lg ${
                  selectedLog.has_notes || selectedLog.manually_marked_compliant 
                    ? 'bg-emerald-50 border border-emerald-200' 
                    : 'bg-red-50 border border-red-200'
                }`}>
                  {selectedLog.has_notes || selectedLog.manually_marked_compliant ? (
                    <div className="flex items-center gap-2 text-emerald-700">
                      <CheckCircle2 className="h-5 w-5" />
                      <span className="font-medium">Compliant - Notes recorded in CEIPAL</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-red-700">
                      <XCircle className="h-5 w-5" />
                      <span className="font-medium">Non-Compliant - No notes found</span>
                    </div>
                  )}
                </div>
              </div>
              
              {selectedLog.notes_content && (
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Notes Content</p>
                  <div className="p-3 bg-muted/50 rounded-lg text-sm">
                    {selectedLog.notes_content}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
